import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react"; 
import Modal from "react-modal";
import { Helmet } from 'react-helmet-async';

Modal.setAppElement("#root");

const noScrollbarStyles = `
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
`;

interface Product {
  id: number;
  name: string;
  image: string;
  price: number;
  offer?: string;
  rating: number;
  isSubscribable?: boolean;
}

const API_BASE_URL = 'https://ayushvatika.com';
const IMAGE_BASE_URL = 'https://ayushvatika.com/images/products/';

const CategoryPage = () => {
  const navigate = useNavigate();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [zoomedProduct, setZoomedProduct] = useState<Product | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedSize, setSelectedSize] = useState<string>('60');
  const [isSubscribing, setIsSubscribing] = useState<boolean>(false);
  const [selectedDeliveryDays, setSelectedDeliveryDays] = useState<number>(30);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/products`);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP error! status: ${response.status} - ${errorText}`
          );
        }
        const data = await response.json();
        const formattedProducts = data.map((product: any) => ({
          id: product.id,
          name: product.productName,
          image: `${IMAGE_BASE_URL}${product.imageUrl}`,
          price: product.price,
          offer: product.offer,
          rating: product.rating,
          isSubscribable: product.productName.toLowerCase().includes('ashwagandha') || product.productName.toLowerCase().includes('lion\'s mane'),
        }));
        setProducts(formattedProducts);
      } catch (e) {
        if (e instanceof Error) {
          console.error("Failed to fetch products for category page:", e);
          setError(e.message);
        } else {
          console.error("An unknown error occurred while fetching products:", e);
          setError("An unknown error occurred");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const scroll = (offset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  const openZoom = (product: Product) => {
    setZoomedProduct(product);
    setModalOpen(true);
    setSelectedSize('60');
    setIsSubscribing(false);
    setSelectedDeliveryDays(30);
  };

  const closeZoom = () => {
    setModalOpen(false);
    setZoomedProduct(null);
  };

  const handleAddToCart = (productId: number) => {
    console.log(`Product with ID ${productId} added to cart.`);
  };

  const handleViewDetails = (productId: number) => {
    closeZoom();
    navigate(`/product/${productId}`);
  };

  const getCalculatedPrice = () => {
    if (!zoomedProduct) return 0;
    let basePrice = zoomedProduct.price;
    if (selectedSize === '120') basePrice = basePrice * 1.5;
    else if (selectedSize === '180') basePrice = basePrice * 2.0;

    if (isSubscribing) return (basePrice * 0.85).toFixed(2);
    return basePrice.toFixed(2);
  };

  const getSizeInDays = (size: string) => {
    switch (size) {
      case '60': return '60-day';
      case '120': return '120-day';
      case '180': return '180-day';
      default: return '40-day';
    }
  };


  if (isLoading) {
    return (
      <div className="text-center p-8 text-green-700">Loading products...</div>
    );
  }

  if (error) {
    return <div className="text-center p-8 text-red-600">Error: {error}</div>;
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-8 overflow-x-hidden bg-gray-50">
      <style>{noScrollbarStyles}</style>

      <Helmet>
        <title>Best Ayurvedic Medicines & Herbal Supplements | Ayush Vatika</title>
        <meta name="description" content="Shop trending Ayurvedic medicines, immunity boosters, and herbal supplements at Ayush Vatika. 100% natural and authentic remedies." />
        <meta name="keywords" content="Ayurvedic medicines, herbal supplements, immunity boosters, natural remedies, Ayush Vatika trending" />
      </Helmet>

      {/* Header Section */}
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-2xl font-semibold text-gray-800 flex items-center flex-wrap">
          <span className="hidden sm:inline">Trending Ayurvedic Medicines & Supplements from&nbsp;</span>
          <span className="sm:hidden">Trending Herbs from&nbsp;</span>
          <span className="text-green-700 font-bold border-b-2 border-green-700">
            AyushVatika
          </span>
        </h2>
        <button
          onClick={() => navigate("/all-products")}
          className="text-xs sm:text-sm text-gray-600 font-semibold uppercase hover:text-green-800 whitespace-nowrap ml-2"
          aria-label="View all Ayurvedic products and herbal medicines"
        >
          View All
        </button>
      </div>

      {/* Products Display */}
      <div className="relative">
        <button
          className="absolute left-0 top-1/2 transform -translate-y-1/2 -ml-3 z-10 bg-white shadow-lg p-2 rounded-full hidden md:block"
          onClick={() => scroll(-250)}
          aria-label="Scroll left"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button
          className="absolute right-0 top-1/2 transform -translate-y-1/2 -mr-3 z-10 bg-white shadow-lg p-2 rounded-full hidden md:block"
          onClick={() => scroll(250)}
          aria-label="Scroll right"
        >
          <ArrowRight className="w-5 h-5" />
        </button>

        <div
          ref={scrollRef}
          className="grid grid-cols-2 gap-3 md:flex md:gap-6 md:overflow-x-auto no-scrollbar md:py-4 md:px-2"
          style={{ scrollBehavior: "smooth" }}
        >
          {products.map((product) => (
            <div
              key={product.id}
              className="flex-shrink-0 w-full md:w-56 bg-white rounded-md overflow-hidden transition-transform duration-300 transform-gpu hover:scale-105 cursor-pointer flex flex-col items-center p-3 sm:p-4 border border-gray-200"
              onClick={() => openZoom(product)}
            >
              <div className="h-32 sm:h-40 w-full flex items-center justify-center bg-gray-50 rounded mb-2">
                <img
                  src={product.image}
                  alt={`${product.name} - Authentic Ayurvedic Medicine`}
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                />
              </div>

              <div className="text-center w-full flex flex-col justify-between flex-grow">
                <div>
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 line-clamp-2 h-8 sm:h-auto overflow-hidden">
                    {product.name}
                  </h3>
                  {product.price && (
                    <p className="text-sm font-bold text-gray-800 mt-1 sm:mt-2">
                      ₹{product.price}
                    </p>
                  )}
                </div>
                
                <button
                  className="mt-2 sm:mt-4 w-full text-white bg-green-800 hover:bg-green-900 font-medium px-2 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm transition"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart(product.id);
                  }}
                  aria-label={`Add ${product.name} to bag`}
                >
                  ADD TO BAG
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Section */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={closeZoom}
        contentLabel="Product Options"
        className="relative w-[95%] sm:w-[500px] max-w-xl mx-auto my-20 bg-white rounded-xl shadow-2xl p-4 sm:p-6 outline-none max-h-[90vh] overflow-y-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      >
        {zoomedProduct && (
          <div className="flex flex-col items-center text-center">
            <button
              onClick={closeZoom}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              aria-label="Close product view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-4 mb-2">{zoomedProduct.name}</h2>
            
            {/* REMOVED: Small View Full Details Link was here */}

            <img
              src={zoomedProduct.image}
              alt={`Buy ${zoomedProduct.name} Online`}
              className="w-24 h-24 sm:w-32 sm:h-32 object-contain rounded-full mb-4"
            />
            
             <div className="w-full mb-4">
              <p className="text-sm uppercase tracking-wide text-gray-500 mb-2">Size: {getSizeInDays(selectedSize)} supply</p>
              <div className="flex justify-center gap-2">
                {['60', '120', '180'].map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`px-4 py-1.5 sm:px-6 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition ${selectedSize === size ? 'bg-green-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div className="w-full mb-6 text-left">
              <label
                onClick={() => setIsSubscribing(false)}
                className={`flex items-center justify-between p-3 sm:p-4 bg-white rounded-lg cursor-pointer mb-2 transition hover:bg-gray-50 ${!isSubscribing ? 'border border-green-700 shadow-sm' : 'border border-gray-300'}`}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="purchase"
                    checked={!isSubscribing}
                    className="form-radio text-green-600 h-4 w-4 sm:h-5 sm:w-5"
                    onChange={() => {}}
                  />
                  <span className="ml-3 text-sm sm:text-base font-medium text-gray-800">One Time Purchase</span>
                </div>
                <span className="font-semibold text-sm sm:text-base text-gray-900">₹{zoomedProduct.price.toFixed(2)}</span>
              </label>

              {zoomedProduct.isSubscribable && (
                <label
                  onClick={() => setIsSubscribing(true)}
                  className={`block p-3 sm:p-4 bg-white rounded-lg cursor-pointer transition hover:bg-green-50 ${isSubscribing ? 'border border-green-700 shadow-sm' : 'border border-gray-300'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="purchase"
                        checked={isSubscribing}
                        className="form-radio text-green-600 h-4 w-4 sm:h-5 sm:w-5"
                        onChange={() => {}}
                      />
                      <span className="ml-3 text-sm sm:text-base font-medium text-gray-800">Subscribe & Save 15%</span>
                    </div>
                    <span className="font-semibold text-sm sm:text-base text-green-700">₹{getCalculatedPrice()}</span>
                  </div>
                  
                  {isSubscribing && (
                    <div className="mt-3 text-xs sm:text-sm text-gray-600">
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Free US shipping</li>
                        <li>Charged at selected frequency</li>
                      </ul>
                    </div>
                  )}
                </label>
              )}
            </div>

            {isSubscribing && (
              <div className="w-full mb-6 text-left">
                <p className="text-sm uppercase tracking-wide text-gray-500 mb-2">Deliver every:</p>
                <div className="flex justify-between gap-1 sm:gap-2">
                  {[30, 60, 90, 120].map((days) => (
                    <button
                      key={days}
                      onClick={() => setSelectedDeliveryDays(days)}
                      className={`flex-1 px-2 py-1.5 rounded-full text-[10px] sm:text-xs font-semibold transition ${selectedDeliveryDays === days ? 'bg-green-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      {days} days
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CHANGED: Replaced 'ADD TO BAG' with 'VIEW FULL DETAILS' */}
            <button
              className="w-full bg-green-800 text-white py-2 sm:py-3 rounded-full font-semibold text-base sm:text-lg hover:bg-green-900 transition-colors shadow-md"
              onClick={() => handleViewDetails(zoomedProduct.id)}
            >
              VIEW FULL DETAILS
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CategoryPage;